// src/pages/AdminAttendancePage.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Button, TextField, MenuItem, Select, FormControl, InputLabel,
  Typography, Stack, Paper, Snackbar, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import axios from 'axios';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import jalaliday from 'jalaliday';
import 'dayjs/locale/fa';

import {
  getAdminLessons,
  getAdminAbsents,
  getAttendance,
  getStudentAttendanceHistory
} from '../api/attendanceApi';
import { gregorianToJalaaliString } from '../utils/jalaaliHelper';
import { getAuthHeader } from '../utils/authHelper';

dayjs.extend(jalaliday);
dayjs.locale('fa');

const BASE = process.env.REACT_APP_API_BASE_URL || 'https://education-backend.liara.run';
const rowsPerPage = 200;

const statusToFarsi = (status = '') => {
  const s = String(status).toLowerCase();
  if (s === 'present') return 'حاضر';
  if (s === 'absent') return 'غایب';
  if (s === 'excused') return 'موجه';
  if (s === 'late') return 'تاخیر';
  return status || '-';
};

export default function AdminAttendancePage() {
  const [lessons, setLessons] = useState([]);
  const [lessonId, setLessonId] = useState('');
  const [sessionIndex, setSessionIndex] = useState(1);
  const [internalDateIso, setInternalDateIso] = useState(new Date().toISOString().slice(0, 10));
  const [snack, setSnack] = useState({ open: false, msg: '' });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);

  // search
  const [searchQuery, setSearchQuery] = useState('');

  // student history dialog state
  const [selectedStudent, setSelectedStudent] = useState(null); // { id, name }
  const [studentHistory, setStudentHistory] = useState([]); // enriched history
  const [studentHistoryLoading, setStudentHistoryLoading] = useState(false);
  const [studentFilter, setStudentFilter] = useState('all'); // 'all' | 'absent' | 'present' | 'late' | 'excused'

  useEffect(() => {
    (async () => {
      try {
        const arr = await getAdminLessons();
        const normalized = Array.isArray(arr) ? arr.map(l => ({ id: l.id ?? l.Id, title: l.title ?? l.Title })) : [];
        setLessons(normalized);
        if (!normalized.length) {
          setSnack({ open: true, msg: 'لیست درس‌ها خالی است — در صورت نیاز شناسه درس را وارد کنید یا اندپوینت admin/lessons را فعال کنید' });
        }
      } catch (e) {
        console.error('load lessons failed', e);
        setLessons([]);
        setSnack({ open: true, msg: 'بارگذاری لیست درس‌ها ناموفق بود' });
      }
    })();
  }, []);

  const effectiveLessons = useMemo(() => {
    if (lessonId) {
      const found = lessons.find(l => String(l.id) === String(lessonId));
      return found ? [found] : [{ id: Number(lessonId), title: `درس ${lessonId}` }];
    }
    return lessons;
  }, [lessonId, lessons]);

  const fetchParentsForStudents = useCallback(async (studentIds = []) => {
    if (!studentIds.length) return new Map();
    try {
      const tokenHeaders = getAuthHeader();
      const batchUrl = `${BASE}/api/students/parents?ids=${studentIds.join(',')}`;
      const res = await axios.get(batchUrl, { headers: tokenHeaders, validateStatus: s => s < 500 });
      if (res.status === 200 && Array.isArray(res.data)) {
        const map = new Map();
        res.data.forEach(x => map.set(Number(x.studentId), x.parents || []));
        return map;
      }
    } catch (e) {
      console.warn('batch parents endpoint not available, falling back', e);
    }
    const map = new Map();
    await Promise.all(studentIds.map(async sid => {
      try {
        const res = await axios.get(`${BASE}/api/student/${sid}/parents`, { headers: getAuthHeader(), validateStatus: s => s < 500 });
        map.set(Number(sid), res.status === 200 && Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        map.set(Number(sid), []);
      }
    }));
    return map;
  }, []);

  const loadAbsents = useCallback(async () => {
    if (!internalDateIso) {
      setSnack({ open: true, msg: 'تاریخ را انتخاب کنید' });
      return;
    }
    setLoading(true);
    try {
      const role = localStorage.getItem('teacherRole');

      if (role === 'Admin') {
        try {
          const data = await getAdminAbsents({ date: internalDateIso, session: sessionIndex, lessonId: lessonId ? Number(lessonId) : undefined });
          const items = Array.isArray(data.items) ? data.items : [];

          const studentIds = [...new Set(items.map(it => Number(it.studentId ?? it.student_id)).filter(Boolean))];
          const parentsMap = await fetchParentsForStudents(studentIds);

          const mapped = items.map(it => {
            const sid = Number(it.studentId ?? it.student_id);
            return {
              studentId: sid,
              studentName: it.studentName ?? it.student_name ?? sid,
              parents: it.parents ?? parentsMap.get(sid) ?? [],
              lessonId: it.lessonId ?? it.lesson_id ?? null,
              lessonTitle: it.lessonTitle ?? it.lesson_title ?? '',
              teacherId: it.teacherId ?? it.teacher_id ?? null,
              date: typeof it.date === 'string' ? it.date : (it.date ? new Date(it.date).toISOString().slice(0,10) : internalDateIso),
              sessionIndex: it.sessionIndex ?? it.session_index ?? sessionIndex,
              status: it.status ?? it.Status ?? 'absent',
              note: it.note ?? it.Note ?? ''
            };
          });

          console.log('admin mapped rows:', mapped);
          setRows(mapped);
          if (!mapped.length) setSnack({ open: true, msg: 'برای این فیلترها غایبی یافت نشد' });
          setLoading(false);
          return;
        } catch (err) {
          console.error('getAdminAbsents failed', err);
          setSnack({ open: true, msg: 'اندپوینت تجمیعی ادمین در دسترس نیست، تلاش با داده‌های درسی' });
        }
      }

      const tasks = (effectiveLessons || []).map(l => {
        const id = Number(l.id);
        return getAttendance(id, internalDateIso, Number(sessionIndex))
          .then(res => ({ lessonId: id, lessonTitle: l.title ?? l.Title, items: Array.isArray(res?.items) ? res.items : [] }))
          .catch(err => {
            console.error('getAttendance failed for lesson', id, err);
            return { lessonId: id, lessonTitle: l.title ?? l.Title, items: [] };
          });
      });

      const all = await Promise.all(tasks);

      const absentEntries = [];
      const studentIdsToFetch = new Set();
      for (const block of all) {
        for (const it of block.items) {
          const statusNorm = String(it.status ?? it.Status ?? '').toLowerCase();
          if (statusNorm === 'absent') {
            const sid = Number(it.studentId ?? it.student_id ?? it.StudentId);
            studentIdsToFetch.add(sid);
            absentEntries.push({
              studentId: sid,
              lessonId: block.lessonId,
              lessonTitle: block.lessonTitle,
              teacherId: it.teacherId ?? it.teacher_id ?? null,
              date: typeof it.date === 'string' ? it.date : (it.date ? new Date(it.date).toISOString().slice(0,10) : internalDateIso),
              sessionIndex: it.sessionIndex ?? it.session_index ?? sessionIndex,
              status: it.status ?? it.Status ?? 'absent',
              note: it.note ?? it.Note ?? ''
            });
          }
        }
      }

      const sids = [...studentIdsToFetch].filter(Boolean);
      const parentsMap = sids.length ? await fetchParentsForStudents(sids) : new Map();

      const mapped = absentEntries.map(a => ({
        ...a,
        studentName: a.studentName ?? a.studentId,
        parents: parentsMap.get(a.studentId) ?? []
      }));

      console.log('per-lesson mapped rows:', mapped);
      setRows(mapped);
      if (!mapped.length) setSnack({ open: true, msg: 'برای این فیلترها غایبی یافت نشد' });
    } catch (err) {
      console.error('loadAbsents failed', err);
      setRows([]);
      setSnack({ open: true, msg: 'خطا در بارگذاری غایبین' });
    } finally {
      setLoading(false);
    }
  }, [effectiveLessons, fetchParentsForStudents, internalDateIso, lessonId, lessons, sessionIndex]);

  const exportCsv = useCallback(() => {
    if (!rows.length) { setSnack({ open: true, msg: 'داده‌ای برای خروجی وجود ندارد' }); return; }
    const header = ['studentId', 'studentName', 'lessonId', 'lessonTitle', 'teacherId', 'date', 'sessionIndex', 'status', 'note', 'parents'];
    const lines = [ header.join(','), ...rows.map(r => [
        r.studentId,
        `"${String(r.studentName ?? '').replace(/"/g,'""')}"`,
        r.lessonId,
        `"${String(r.lessonTitle ?? '').replace(/"/g,'""')}"`,
        r.teacherId ?? '',
        r.date,
        r.sessionIndex,
        `"${statusToFarsi(r.status)}"`,
        `"${String(r.note ?? '').replace(/"/g,'""')}"`,
        `"${JSON.stringify(r.parents ?? []).replace(/"/g,'""')}"` 
    ].join(',')) ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url;
    a.download = `absents_${internalDateIso}_session${sessionIndex}_${lessonId || 'all'}.csv`; a.click(); URL.revokeObjectURL(url);
  }, [internalDateIso, rows, sessionIndex, lessonId]);

  const exportParentPhonesTxt = useCallback(() => {
    const phones = new Set();
    rows.forEach(r => {
      (r.parents || []).forEach(p => {
        const ph = p.phone || p.mobile || p.phoneNumber || p.Phone || '';
        if (ph) phones.add(ph.trim());
      });
    });
    const arr = [...phones];
    if (!arr.length) { setSnack({ open: true, msg: 'شماره والدین پیدا نشد' }); return; }
    const blob = new Blob([arr.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url;
    a.download = `parent_phones_${internalDateIso}_session${sessionIndex}.txt`; a.click(); URL.revokeObjectURL(url);
  }, [internalDateIso, rows, sessionIndex]);

  const copyParentPhonesToClipboard = useCallback(async () => {
    const phones = new Set();
    rows.forEach(r => {
      (r.parents || []).forEach(p => {
        const ph = p.phone || p.mobile || p.phoneNumber || p.Phone || '';
        if (ph) phones.add(ph.trim());
      });
    });
    const arr = [...phones];
    if (!arr.length) { setSnack({ open: true, msg: 'شماره‌ای برای کپی یافت نشد' }); return; }
    try { await navigator.clipboard.writeText(arr.join('\n')); setSnack({ open: true, msg: 'شماره‌ها در کلیپ‌بورد کپی شد' }); }
    catch { setSnack({ open: true, msg: 'کپی به کلیپ‌بورد ممکن نشد' }); }
  }, [rows]);

  // client-side search across studentName, lessonTitle, studentId, parent name/phone/email
  const filteredRows = useMemo(() => {
    if (!searchQuery || !String(searchQuery).trim()) return rows;
    const q = String(searchQuery).trim().toLowerCase();
    return rows.filter(r => {
      const studentName = String(r.studentName ?? '').toLowerCase();
      if (studentName.includes(q)) return true;
      const lessonTitle = String(r.lessonTitle ?? '').toLowerCase();
      if (lessonTitle.includes(q)) return true;
      if (String(r.studentId ?? '').toLowerCase().includes(q)) return true;
      if (Array.isArray(r.parents)) {
        for (const p of r.parents) {
          const pn = String(p.name ?? p.Name ?? '').toLowerCase();
          const phone = String(p.phone ?? p.Phone ?? '').toLowerCase();
          const email = String(p.email ?? p.Email ?? '').toLowerCase();
          if (pn.includes(q) || phone.includes(q) || email.includes(q)) return true;
        }
      }
      return false;
    });
  }, [rows, searchQuery]);

  const visibleRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [page, filteredRows]);

  // helper to convert internal ISO to dayjs jalali object
  const isoToDayjsJalali = (iso) => {
    if (!iso) return null;
    return dayjs(iso).calendar('jalali');
  };

  // loadStudentHistory with enrichment of lesson titles
  const loadStudentHistory = useCallback(async (studentId) => {
    setStudentHistoryLoading(true);
    try {
      const resp = await getStudentAttendanceHistory(studentId); // expected array
      const normalized = Array.isArray(resp) ? resp.map(it => ({
        lessonId: it.lessonId ?? it.lesson_id ?? it.LessonId,
        lessonTitle: it.lessonTitle ?? it.lesson_title ?? '',
        date: typeof it.date === 'string' ? it.date : (it.date ? new Date(it.date).toISOString().slice(0,10) : ''),
        sessionIndex: it.sessionIndex ?? it.session_index ?? it.SessionIndex ?? 1,
        status: (it.status ?? it.Status ?? '').toString(),
        note: it.note ?? it.Note ?? ''
      })) : [];

      const lessonIds = [...new Set(normalized.map(x => x.lessonId).filter(Boolean))];
      const lessonMap = new Map();
      lessons.forEach(l => lessonMap.set(Number(l.id), l.title || ''));

      const missingIds = lessonIds.filter(id => !lessonMap.has(Number(id)));
      if (missingIds.length) {
        try {
          const allLessons = await getAdminLessons();
          if (Array.isArray(allLessons)) {
            allLessons.forEach(l => lessonMap.set(Number(l.id), l.title ?? l.Title ?? ''));
          }
        } catch (e) {
          console.warn('failed to fetch all lessons for titles enrichment', e);
        }
      }

      const enriched = normalized.map(item => ({
        ...item,
        lessonTitle: item.lessonTitle && item.lessonTitle.trim() ? item.lessonTitle : (lessonMap.get(Number(item.lessonId)) || '')
      }));

      setStudentHistory(enriched);
    } catch (err) {
      console.error('loadStudentHistory failed', err);
      setStudentHistory([]);
      setSnack({ open: true, msg: 'خطا در بارگذاری سابقهٔ دانش‌آموز' });
    } finally {
      setStudentHistoryLoading(false);
    }
  }, [lessons]);

  // derive filtered history according to studentFilter
  const filteredStudentHistory = useMemo(() => {
    if (!studentHistory || !studentHistory.length) return [];
    if (studentFilter === 'all') return studentHistory;
    const want = studentFilter.toLowerCase();
    return studentHistory.filter(h => String(h.status || '').toLowerCase() === want);
  }, [studentHistory, studentFilter]);

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="lesson-select-label">درس</InputLabel>
            <Select labelId="lesson-select-label" value={lessonId} label="درس" onChange={(e) => { setLessonId(e.target.value); setPage(1); }}>
              <MenuItem value=''>همهٔ درس‌ها</MenuItem>
              {lessons.map(l => <MenuItem key={l.id} value={l.id}>{l.title}</MenuItem>)}
            </Select>
          </FormControl>

          {!lessons.length && (
            <TextField label="شناسه درس (موقت)" type="number" value={lessonId} onChange={(e) => setLessonId(e.target.value)} sx={{ width: 180 }} />
          )}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="تاریخ (هجری)"
              value={isoToDayjsJalali(internalDateIso)}
              onChange={(newVal) => {
                if (!newVal) { setInternalDateIso(''); return; }
                const greg = dayjs(newVal).calendar('gregory');
                const iso = greg.format('YYYY-MM-DD');
                setInternalDateIso(iso);
              }}
              renderInput={(params) => <TextField {...params} sx={{ width: 160 }} />}
              inputFormat="YYYY/MM/DD"
              disableMaskedInput={false}
              cancelText="لغو"
              clearText="پاک کن"
              okText="تایید"
            />
          </LocalizationProvider>

          <FormControl sx={{ width: 140 }}>
            <InputLabel id="session-select-label">جلسه</InputLabel>
            <Select value={sessionIndex} labelId="session-select-label" onChange={(e) => setSessionIndex(Number(e.target.value))}>
              <MenuItem value={1}>جلسه 1</MenuItem>
              <MenuItem value={2}>جلسه 2</MenuItem>
              <MenuItem value={3}>جلسه 3</MenuItem>
              <MenuItem value={4}>جلسه 4</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="جستجو (نام، والد، درس، یا ID)"
            size="small"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            sx={{ minWidth: 300 }}
            placeholder="مثال: امیر یا کار آفرینی یا 142"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearchQuery(''); setPage(1); }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Button disabled={loading} variant="contained" onClick={loadAbsents} startIcon={<FilterAltIcon />}>
            {loading ? <><CircularProgress size={16} sx={{ mr: 1 }} />در حال بارگذاری</> : 'اعمال فیلتر'}
          </Button>

          <Button disabled={!rows.length} variant="outlined" color="primary" onClick={exportCsv} startIcon={<DownloadIcon />}>خروجی CSV</Button>

          <Button disabled={!rows.length} variant="outlined" color="secondary" onClick={exportParentPhonesTxt}>خروجی شماره والدین (TXT)</Button>

          <IconButton onClick={copyParentPhonesToClipboard} title="کپی شماره‌ها به کلیپ‌بورد"><ContentCopyIcon /></IconButton>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          تاریخ میلادی: {internalDateIso} — هجری: {gregorianToJalaaliString(internalDateIso)} — جلسه: {sessionIndex} — درس: {lessonId || 'همه'}
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">لیست غایبین</Typography>
          <Chip label={`${rows.length} مورد`} size="small" />
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>دانش‌آموز</TableCell>
              <TableCell>والدین (نام / شماره / ایمیل)</TableCell>
              <TableCell>درس</TableCell>
              <TableCell>تاریخ (هجری)</TableCell>
              <TableCell>جلسه</TableCell>
              <TableCell>وضعیت</TableCell>
              <TableCell>یادداشت</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((r, idx) => (
              <TableRow key={`${r.studentId}-${r.lessonId}-${r.date}-${r.sessionIndex}-${idx}`}>
                <TableCell>
                  <div
                    style={{ fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', direction: 'rtl' }}
                    onClick={() => {
                      setSelectedStudent({ id: r.studentId, name: r.studentName ?? r.studentId });
                      setStudentFilter('absent'); // open dialog showing only absents by default
                      loadStudentHistory(r.studentId);
                    }}
                  >
                    {r.studentName ?? r.studentId}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>ID: {r.studentId}</div>
                </TableCell>
                <TableCell>
                  {(r.parents || []).length
                    ? r.parents.map((p, i) => (
                        <div key={i} style={{ marginBottom: 6 }}>
                          <div style={{ fontWeight: 600 }}>{p.name || p.Name || '-'}</div>
                          <div style={{ fontSize: 13 }}>{p.phone || p.Phone || '-'}</div>
                          <div style={{ fontSize: 12, color: '#555' }}>{p.email || p.Email || '-'}</div>
                        </div>
                      ))
                    : <div style={{ color: '#999' }}>والدی ثبت نشده</div>}
                </TableCell>
                <TableCell>{r.lessonTitle ? r.lessonTitle : `درس ${r.lessonId}`}</TableCell>
                <TableCell>{gregorianToJalaaliString(r.date)}</TableCell>
                <TableCell>{r.sessionIndex}</TableCell>
                <TableCell>
                  <Chip label={statusToFarsi(r.status)} color={String(r.status).toLowerCase() === 'absent' ? 'error' : 'default'} size="small" />
                </TableCell>
                <TableCell>{r.note || ''}</TableCell>
              </TableRow>
            ))}

            {!rows.length && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ color: '#777' }}>موردی یافت نشد.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {rows.length > rowsPerPage && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
            <Button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>قبلی</Button>
            <Typography>{page}</Typography>
            <Button disabled={page * rowsPerPage >= rows.length} onClick={() => setPage(p => p + 1)}>بعدی</Button>
          </Stack>
        )}
      </Paper>

      <Dialog open={!!selectedStudent} onClose={() => setSelectedStudent(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedStudent ? `سابقه ${studentFilter === 'all' ? '' : statusToFarsi(studentFilter) + ' '}${selectedStudent.name}` : 'سابقه دانش‌آموز'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="student-filter-label">فیلتر وضعیت</InputLabel>
              <Select
                labelId="student-filter-label"
                value={studentFilter}
                label="فیلتر وضعیت"
                onChange={(e) => setStudentFilter(e.target.value)}
              >
                <MenuItem value="all">همه</MenuItem>
                <MenuItem value="absent">فقط غایب</MenuItem>
                <MenuItem value="present">فقط حاضر</MenuItem>
                <MenuItem value="late">فقط تاخیر</MenuItem>
                <MenuItem value="excused">فقط موجه</MenuItem>
              </Select>
            </FormControl>

            <Button variant="outlined" size="small" onClick={() => {
              if (selectedStudent?.id) loadStudentHistory(selectedStudent.id);
            }}>
              تازه‌سازی
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              نمایش: {studentFilter === 'all' ? 'همه' : statusToFarsi(studentFilter)}
            </Typography>
          </Stack>

          {studentHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
          ) : (
            <>
              {filteredStudentHistory.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>درس</TableCell>
                      <TableCell>تاریخ (میلادی)</TableCell>
                      <TableCell>تاریخ (هجری)</TableCell>
                      <TableCell>جلسه</TableCell>
                      <TableCell>وضعیت</TableCell>
                      <TableCell>یادداشت</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStudentHistory.map((h, i) => (
                      <TableRow key={`${h.lessonId}-${h.date}-${i}`}>
                        <TableCell>{h.lessonTitle ? h.lessonTitle : `درس ${h.lessonId}`}</TableCell>
                        <TableCell>{h.date}</TableCell>
                        <TableCell>{gregorianToJalaaliString(h.date)}</TableCell>
                        <TableCell>{h.sessionIndex}</TableCell>
                        <TableCell>
                          <Chip label={statusToFarsi(h.status)} color={String(h.status).toLowerCase() === 'absent' ? 'error' : 'default'} size="small" />
                        </TableCell>
                        <TableCell>{h.note || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography sx={{ color: '#666' }}>موردی یافت نشد.</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedStudent(null)}>بستن</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })} message={snack.msg} />
    </Box>
  );
}